/**
 * A pop-up for the deposit screens: centred on a wide screen, a sheet from the bottom on a phone.
 * Esc, the close button and a click outside close it; focus returns to where it was.
 */
import { useEffect, useId, useRef, type ReactNode } from 'react';
import { Icon } from '../../../components/ui';

export function Sheet({ title, onClose, children }: { title: ReactNode; onClose: () => void; children: ReactNode }) {
  const id = useId();
  const box = useRef<HTMLDivElement>(null);
  const close = useRef(onClose);
  close.current = onClose;
  useEffect(() => {
    const prev = document.activeElement as HTMLElement | null;
    box.current?.querySelector<HTMLElement>('button')?.focus();
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && close.current();
    const overflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    window.addEventListener('keydown', onKey);
    return () => {
      window.removeEventListener('keydown', onKey);
      document.body.style.overflow = overflow;
      prev?.focus?.();
    };
  }, []);
  return (
    <div className="fixed inset-0 z-50 grid place-items-end bg-scrim sm:place-items-center sm:p-4" onMouseDown={(e) => e.target === e.currentTarget && onClose()}>
      <div
        ref={box}
        role="dialog"
        aria-modal="true"
        aria-labelledby={id}
        className="shadow-pop max-h-[92dvh] w-full overflow-y-auto rounded-t-[28px] bg-card p-6 sm:max-w-[600px] sm:rounded-[28px] md:p-8"
      >
        <div className="flex items-center justify-between gap-4">
          <h2 id={id} className="m-0 text-[22px] font-semibold md:text-[24px]">
            {title}
          </h2>
          <button type="button" onClick={onClose} aria-label="Close" className="grid size-10 shrink-0 place-items-center rounded-full bg-pill text-fg">
            <Icon name="close" size={16} />
          </button>
        </div>
        {children}
      </div>
    </div>
  );
}
